import { shopTheme } from "../functions/shopTheme";

const readImage = source => new Promise((resolve, reject) => {
  const image = new Image();
  const timer = setTimeout(() => { image.onload = null; image.onerror = null; reject(new Error("Icon timeout")); }, 6000);
  image.crossOrigin = "anonymous";
  image.onload = () => { clearTimeout(timer); resolve(image); };
  image.onerror = () => { clearTimeout(timer); reject(new Error("Icon unavailable")); };
  image.src = source;
});
export const merchantInitials = name => name.trim().split(/\s+/).slice(0, 2).map(word => [...word][0]).join("").toUpperCase();
export const installImageSource = source => {
  const url = new URL(source, window.location.origin);
  // Our public CDN does not allow canvas reads across origins. Serve only
  // merchant branding through the same-origin, fixed-upstream asset route.
  return url.origin === "https://cdn.delibran.app" && /^\/corporations\/[a-f0-9-]{36}\/configuration\/(logo|favicon)\.(png|jpe?g|webp|svg)$/i.test(url.pathname)
    ? `/shop-brand-assets${url.pathname}` : url.href;
};
export const prepareIcons = async ({ name, favIcon, logo, color }) => {
  const theme = shopTheme(color);
  const render = (size, image) => {
    const canvas = document.createElement("canvas"); canvas.width = size; canvas.height = size;
    const context = canvas.getContext("2d");
    context.fillStyle = image ? "#ffffff" : theme["--eats-accent"]; context.fillRect(0, 0, size, size);
    if (image) {
      const scale = size * .84 / Math.max(image.naturalWidth, image.naturalHeight);
      const width = image.naturalWidth * scale, height = image.naturalHeight * scale;
      context.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
    } else {
      context.fillStyle = theme["--eats-accent-text"]; context.font = `700 ${Math.round(size * .34)}px sans-serif`;
      context.textAlign = "center"; context.textBaseline = "middle"; context.fillText(merchantInitials(name), size / 2, size / 2);
    }
    return canvas.toDataURL("image/png");
  };
  for (const candidate of [...new Set([favIcon, logo].filter(Boolean))]) {
    try {
      const url = new URL(candidate, window.location.origin);
      if (!["https:", "http:"].includes(url.protocol)) continue;
      const image = await readImage(installImageSource(url.href));
      if (!image.naturalWidth || !image.naturalHeight) continue;
      return { 192: render(192, image), 512: render(512, image) };
    } catch { /* Missing or cross-origin image: try the commerce logo next. */ }
  }
  if (favIcon || logo) throw new Error("Merchant icon unavailable");
  return { 192: render(192), 512: render(512) };
};
const meta = (name, content) => {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) { element = document.createElement("meta"); element.name = name; document.head.appendChild(element); }
  element.content = content;
};
export const configureShopInstall = async (brand, isCurrent = () => true) => {
  if (!window.isSecureContext || !("serviceWorker" in navigator)) throw new Error("Installation unavailable");
  if (!/^[a-f0-9-]{36}$/i.test(brand.corporationId || "")) throw new Error("Merchant identity unavailable");
  const path = `/shop-install/${brand.corporationId}`;
  // Android's install agent downloads these URLs outside the page/service worker.
  // They must be real HTTP resources, not entries in a browser-only cache.
  const response = await fetch(`${path}/manifest.webmanifest`, { credentials: "omit", cache: "no-cache" });
  if (!response.ok || !(response.headers.get("content-type") || "").includes("application/manifest+json")) throw new Error("Manifest unavailable");
  const manifestData = await response.json();
  if (manifestData.name !== brand.name) throw new Error("Merchant manifest mismatch");
  await Promise.all([192, 512].map(size => readImage(`${path}/icon-${size}.png`)));
  if (!isCurrent()) return false;
  await navigator.serviceWorker.register("/shop-pwa-sw.js", { scope: "/", updateViaCache: "none" });
  await navigator.serviceWorker.ready;
  if (!navigator.serviceWorker.controller) await new Promise((resolve, reject) => {
    const changed = () => { if (navigator.serviceWorker.controller) { clearTimeout(timer); navigator.serviceWorker.removeEventListener("controllerchange", changed); resolve(); } };
    const timer = setTimeout(() => { navigator.serviceWorker.removeEventListener("controllerchange", changed); reject(new Error("Controller unavailable")); }, 10000);
    navigator.serviceWorker.addEventListener("controllerchange", changed); changed();
  });
  if (!isCurrent()) return false;
  const iconUrl = `${path}/icon-192.png`;
  document.querySelectorAll('link[rel="manifest"]').forEach(element => element.remove());
  const manifest = document.createElement("link"); manifest.rel = "manifest"; manifest.href = `${path}/manifest.webmanifest`; document.head.appendChild(manifest);
  document.querySelectorAll('link[rel="apple-touch-icon"]').forEach(element => element.remove());
  const touch = document.createElement("link"); touch.rel = "apple-touch-icon"; touch.href = iconUrl; touch.dataset.pwaIcon = "true"; document.head.appendChild(touch);
  meta("apple-mobile-web-app-title", brand.name);
  meta("apple-mobile-web-app-capable", "yes");
  document.querySelectorAll('meta[name="theme-color"]').forEach(element => element.remove());
  meta("theme-color", shopTheme(brand.color)["--eats-accent"]);
  document.title = brand.name;
  return { iconUrl };
};
